import React from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';

declare module '@mui/material/styles' {
  interface Theme {
    customShadows: {
      z1: string;
      z8: string;
      z12: string;
      z16: string;
      z20: string;
      z24: string;
      primary: string;
      secondary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
    };
  }
  interface ThemeOptions {
    customShadows?: {
      z1?: string;
      z8?: string;
      z12?: string;
      z16?: string;
      z20?: string;
      z24?: string;
      primary?: string;
      secondary?: string;
      info?: string;
      success?: string;
      warning?: string;
      error?: string;
    };
  }
}

const PRIMARY = {
  lighter: '#D1E9FC',
  light: '#76B0F1',
  main: '#4361EE',
  dark: '#103996',
  darker: '#061B64',
  contrastText: '#FFF',
};

const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3A5BEF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#FFF',
};

const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: '#FFF',
};

const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#FFF',
};

const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: '#212B36',
};

const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#FFF',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: 'rgba(145, 158, 171, 0.08)',
  500_12: 'rgba(145, 158, 171, 0.12)',
  500_16: 'rgba(145, 158, 171, 0.16)',
  500_24: 'rgba(145, 158, 171, 0.24)',
  500_32: 'rgba(145, 158, 171, 0.32)',
  500_48: 'rgba(145, 158, 171, 0.48)',
  500_56: 'rgba(145, 158, 171, 0.56)',
  500_80: 'rgba(145, 158, 171, 0.8)',
};

const COMMON = {
  common: { black: '#000', white: '#FFF' },
  primary: { ...PRIMARY, contrastText: '#FFF' },
  secondary: { ...SECONDARY, contrastText: '#FFF' },
  info: { ...INFO, contrastText: '#FFF' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#FFF' },
  grey: GREY,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const theme = createTheme({
  palette: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#FFF', default: '#F4F6F8' },
    action: { active: GREY[600], ...COMMON.action },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontWeight: 700, lineHeight: 1.2, fontSize: '2.5rem' },
    h2: { fontWeight: 700, lineHeight: 1.2, fontSize: '2rem' },
    h3: { fontWeight: 600, lineHeight: 1.25, fontSize: '1.75rem' },
    h4: { fontWeight: 600, lineHeight: 1.3, fontSize: '1.5rem' },
    h5: { fontWeight: 600, lineHeight: 1.33, fontSize: '1.25rem' },
    h6: { fontWeight: 600, lineHeight: 1.4, fontSize: '1rem' },
    subtitle1: { fontWeight: 500, lineHeight: 1.5, fontSize: '1rem' },
    subtitle2: { fontWeight: 500, lineHeight: 1.57, fontSize: '0.875rem' },
    body1: { lineHeight: 1.5, fontSize: '1rem' },
    body2: { lineHeight: 1.57, fontSize: '0.875rem' },
    caption: { lineHeight: 1.5, fontSize: '0.75rem' },
    overline: {
      fontWeight: 600,
      lineHeight: 1.5,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
    },
    button: { fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize' },
  },
  customShadows: {
    z1: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    z8: '0 8px 16px 0 rgba(0, 0, 0, 0.08)',
    z12: '0 12px 24px -4px rgba(0, 0, 0, 0.12)',
    z16: '0 16px 32px -4px rgba(0, 0, 0, 0.08)',
    z20: '0 20px 40px -4px rgba(0, 0, 0, 0.08)',
    z24: '0 24px 48px 0 rgba(0, 0, 0, 0.16)',
    primary: `0 8px 16px 0 ${alpha(PRIMARY.main, 0.24)}`,
    secondary: `0 8px 16px 0 ${alpha(SECONDARY.main, 0.24)}`,
    info: `0 8px 16px 0 ${alpha(INFO.main, 0.24)}`,
    success: `0 8px 16px 0 ${alpha(SUCCESS.main, 0.24)}`,
    warning: `0 8px 16px 0 ${alpha(WARNING.main, 0.24)}`,
    error: `0 8px 16px 0 ${alpha(ERROR.main, 0.24)}`,
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        color: 'transparent',
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 8px 16px 0 rgba(67, 97, 238, 0.24)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.08), 0 12px 24px -4px rgba(0, 0, 0, 0.08)',
          borderRadius: 16,
          position: 'relative',
          zIndex: 0,
          '&:hover': {
            boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.12), 0 20px 40px -4px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h6' },
        subheaderTypographyProps: { variant: 'body2' },
      },
      styleOverrides: {
        root: {
          padding: 24,
          paddingBottom: 0,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
  },
});

// Extend the theme with custom properties
const customTheme = {
  ...theme,
  customShadows: {
    z1: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    z8: '0 8px 16px 0 rgba(0, 0, 0, 0.08)',
    z12: '0 12px 24px -4px rgba(0, 0, 0, 0.12)',
    z16: '0 16px 32px -4px rgba(0, 0, 0, 0.08)',
    z20: '0 20px 40px -4px rgba(0, 0, 0, 0.08)',
    z24: '0 24px 48px 0 rgba(0, 0, 0, 0.16)',
    primary: `0 8px 16px 0 ${alpha(PRIMARY.main, 0.24)}`,
    secondary: `0 8px 16px 0 ${alpha(SECONDARY.main, 0.24)}`,
    info: `0 8px 16px 0 ${alpha(INFO.main, 0.24)}`,
    success: `0 8px 16px 0 ${alpha(SUCCESS.main, 0.24)}`,
    warning: `0 8px 16px 0 ${alpha(WARNING.main, 0.24)}`,
    error: `0 8px 16px 0 ${alpha(ERROR.main, 0.24)}`,
  },
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export default function CustomThemeProvider({ children }: ThemeProviderProps) {
  return (
    <MuiThemeProvider theme={customTheme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          '*': {
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
          },
          html: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            height: '100%',
            width: '100%',
          },
          body: {
            height: '100%',
            width: '100%',
          },
          '#root': {
            height: '100%',
            width: '100%',
          },
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
}

// Helper function
function alpha(color: string, opacity: number): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
